import { fork, put, takeEvery, call, select, takeLatest, delay, take } from 'redux-saga/effects';
import { EventChannel } from 'redux-saga';
import { StoreActions } from '../../redux/actions';
import {
  fetchStores,
  updateStores,
  mongodbStoresWatchEmitter,
  updateWaiverForm,
} from '../../redux/services/storeService';
import { ActionType } from 'typesafe-actions';
import { RootState } from '../store';
import { IStore } from '../../types';
import { mergeMongodbAndContentfulStores } from '../../utils';
import * as Realm from 'realm-web';
import { IMongodbStore } from '../../types/mongodb';

export function* fetchStoresSaga(): Generator<unknown, void, any> {
  try {
    yield put(StoreActions.fetchStores.start());
    const locations: Awaited<ReturnType<typeof fetchStores>> = yield call(fetchStores);
    yield put(StoreActions.watchStores());
    yield put(StoreActions.fetchStores.success(locations));
  } catch (error) {
    yield put(StoreActions.fetchStores.error(error as Error));
  }
}

export function* watchStoresSaga(): Generator<unknown, void, any> {
  const chan: EventChannel<any> = yield call(mongodbStoresWatchEmitter);
  try {
    while (true) {
      const change = yield take(chan);
      const storesData: IStore[] = yield select((state: RootState) => state.Store.stores.data);

      if (change.operationType === 'insert' || change.operationType === 'update') {
        const fullDocument = Realm.BSON.EJSON.parse(
          JSON.stringify(change.fullDocument),
        ) as IMongodbStore;
        const updatedStores = mergeMongodbAndContentfulStores({
          mongodbStores: [fullDocument],
          contentfulStores: storesData,
        });
        yield put(StoreActions.setStores(updatedStores));
      }
    }
  } catch (error) {
    console.log('error', error);
  } finally {
    chan.close();
  }
}

export function* updateStoresSaga(
  action: ActionType<typeof StoreActions.updateStores.saga>,
): Generator<unknown, void, any> {
  try {
    const { payload } = action;
    yield put(StoreActions.updateStores.start());
    yield call(updateStores, {
      actionType: payload.actionType,
      payload: payload.updatePayload,
    });
    yield put(StoreActions.updateStores.success());
  } catch (error) {
    yield put(StoreActions.updateStores.error(error as Error));
  }
}

export function* updateWaiverFormSaga(
  action: ActionType<typeof StoreActions.updateWaiverForm.saga>,
): Generator<unknown, void, any> {
  try {
    yield delay(500);
    const { payload } = action;
    yield put(StoreActions.updateWaiverForm.start());
    yield call(updateWaiverForm, {
      storeId: payload.storeId,
      content: payload.content,
    });

    yield put(StoreActions.updateWaiverForm.success());
  } catch (error) {
    yield put(StoreActions.updateWaiverForm.error(error as Error));
  }
}

function* fetchStoresSagaListener() {
  yield takeLatest(StoreActions.fetchStores.saga.toString(), fetchStoresSaga);
}

function* updateStoresSagaListener() {
  yield takeEvery(StoreActions.updateStores.saga.toString(), updateStoresSaga);
}

function* updateWaiverFormSagaListener() {
  yield takeLatest(StoreActions.updateWaiverForm.saga.toString(), updateWaiverFormSaga);
}

function* watchStoresSagaListener() {
  yield takeLatest(StoreActions.watchStores.toString(), watchStoresSaga);
}

export default function* storeSaga() {
  yield fork(fetchStoresSagaListener);
  yield fork(updateStoresSagaListener);
  yield fork(watchStoresSagaListener);
  yield fork(updateWaiverFormSagaListener);
}
